import React, { useState, useEffect } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  Select,
  DatePicker,
  message,
  Drawer,
  Modal,
  Form,
  Input,
  Progress,
  Popconfirm
} from 'antd';
import {
  EyeOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import deferredRevenueService, { DeferredRevenue } from '../../../../services/deferredRevenueService';
import ScheduleLineTable from './ScheduleLineTable';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const DeferredRevenueList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DeferredRevenue[]>([]);
  const [filters, setFilters] = useState<any>({});
  const [selectedRevenue, setSelectedRevenue] = useState<DeferredRevenue | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [startModal, setStartModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [form] = Form.useForm();
  const [journals, setJournals] = useState<any[]>([]);

  useEffect(() => {
    fetchData();
    fetchJournals();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await deferredRevenueService.getAll(filters);
      console.log('Fetched deferred revenues:', response);

      // Handle different response structures
      if (Array.isArray(response)) {
        // Response is directly an array
        setData(response);
      } else if (response?.success && response?.data) {
        // Response has success and data properties
        setData(response.data || []);
      } else if (response?.data && Array.isArray(response.data)) {
        // Response has data property that is an array
        setData(response.data);
      } else {
        setData([]);
      }
    } catch (error: any) {
      console.error('Error fetching deferred revenues:', error);
      setData([]);
      // Don't show error message on initial load, just log it
      if (data.length > 0) {
        message.error(error.message || 'Failed to fetch deferred revenues');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchJournals = async () => {
    // TODO: Fetch journals from API
    // For now, using mock data
    setJournals([{ id: '1', code: 'GEN', name: 'General Journal' }]);
  };

  const handleStartRecognition = async (values: any) => {
    if (!selectedRevenue) return;

    try {
      const response = await deferredRevenueService.startRecognition(selectedRevenue.id, values.journalId);
      if (response?.success || response?.id || response?.data) {
        message.success('Recognition started successfully');
        setStartModal(false);
        form.resetFields();
        fetchData();
      } else {
        message.error(response?.message || 'Failed to start recognition');
      }
    } catch (error: any) {
      console.error('Failed to start recognition:', error);
      const errorMessage = error.response?.data?.errors
        ? JSON.stringify(error.response.data.errors, null, 2)
        : error.message || 'Failed to start recognition';
      message.error(errorMessage);
    }
  };

  const handleCancel = async (values: any) => {
    if (!selectedRevenue) return;

    try {
      const response = await deferredRevenueService.cancel(
        selectedRevenue.id,
        values.journalId,
        values.reason
      );
      if (response?.success || response?.id || response?.data) {
        message.success('Deferred revenue cancelled successfully');
        setCancelModal(false);
        form.resetFields();
        fetchData();
      } else {
        message.error(response?.message || 'Failed to cancel deferred revenue');
      }
    } catch (error: any) {
      console.error('Failed to cancel deferred revenue:', error);
      const errorMessage = error.response?.data?.errors
        ? JSON.stringify(error.response.data.errors, null, 2)
        : error.message || 'Failed to cancel deferred revenue';
      message.error(errorMessage);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deferredRevenueService.delete(id);
      // If no error thrown, consider it successful
      message.success('Deferred revenue deleted successfully');
      fetchData();
    } catch (error: any) {
      console.error('Failed to delete deferred revenue:', error);
      const errorMessage = error.response?.data?.errors
        ? JSON.stringify(error.response.data.errors, null, 2)
        : error.message || 'Failed to delete deferred revenue';
      message.error(errorMessage);
    }
  };

  const getStateColor = (state: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'default',
      RUNNING: 'processing',
      FULLY_RECOGNIZED: 'success',
      CANCELLED: 'error'
    };
    return colors[state] || 'default';
  };

  const getRecognitionStateColor = (state: string) => {
    const colors: Record<string, string> = {
      PENDING: 'default',
      PARTIAL: 'warning',
      COMPLETE: 'success'
    };
    return colors[state] || 'default';
  };

  const calculateProgress = (record: DeferredRevenue) => {
    if (record.totalAmount === 0) return 0;
    return Math.round((record.recognizedAmount / record.totalAmount) * 100);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      fixed: 'left' as const
    },
    {
      title: 'Reference',
      key: 'reference',
      width: 150,
      render: (record: DeferredRevenue) => (
        <div>
          <div>{record.referenceType}</div>
          {record.invoice && <div style={{ fontSize: '12px', color: '#888' }}>{record.invoice.invoiceNo}</div>}
        </div>
      )
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      align: 'right' as const,
      render: formatCurrency
    },
    {
      title: 'Recognized',
      dataIndex: 'recognizedAmount',
      key: 'recognizedAmount',
      width: 120,
      align: 'right' as const,
      render: formatCurrency
    },
    {
      title: 'Remaining',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      width: 120,
      align: 'right' as const,
      render: formatCurrency
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 150,
      render: (record: DeferredRevenue) => (
        <Progress percent={calculateProgress(record)} size="small" />
      )
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 120,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 120,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      width: 100
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 120,
      render: (state: string) => <Tag color={getStateColor(state)}>{state}</Tag>
    },
    {
      title: 'Recognition',
      dataIndex: 'recognitionState',
      key: 'recognitionState',
      width: 120,
      render: (state: string) => <Tag color={getRecognitionStateColor(state)}>{state}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 200,
      render: (record: DeferredRevenue) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedRevenue(record);
              setDrawerVisible(true);
            }}
          >
            View
          </Button>
          {record.state === 'DRAFT' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => {
                  setSelectedRevenue(record);
                  setStartModal(true);
                }}
              >
                Start
              </Button>
              <Popconfirm
                title="Delete this deferred revenue?"
                onConfirm={() => handleDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            </>
          )}
          {record.state === 'RUNNING' && (
            <Button
              type="link"
              size="small"
              icon={<StopOutlined />}
              onClick={() => {
                setSelectedRevenue(record);
                setCancelModal(true);
              }}
            >
              Cancel
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Select
            style={{ width: 150 }}
            placeholder="State"
            allowClear
            onChange={(value) => setFilters({ ...filters, state: value })}
          >
            <Option value="DRAFT">Draft</Option>
            <Option value="RUNNING">Running</Option>
            <Option value="FULLY_RECOGNIZED">Fully Recognized</Option>
            <Option value="CANCELLED">Cancelled</Option>
          </Select>
          <Select
            style={{ width: 150 }}
            placeholder="Recognition State"
            allowClear
            onChange={(value) => setFilters({ ...filters, recognitionState: value })}
          >
            <Option value="PENDING">Pending</Option>
            <Option value="PARTIAL">Partial</Option>
            <Option value="COMPLETE">Complete</Option>
          </Select>
          <Select
            style={{ width: 150 }}
            placeholder="Reference Type"
            allowClear
            onChange={(value) => setFilters({ ...filters, referenceType: value })}
          >
            <Option value="INVOICE">Invoice</Option>
            <Option value="MANUAL_ENTRY">Manual Entry</Option>
          </Select>
          <Button onClick={fetchData}>Refresh</Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1800 }}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`
        }}
      />

      {/* Schedule Lines Drawer */}
      <Drawer
        title={`Schedule Lines - ${selectedRevenue?.name}`}
        placement="right"
        width={1000}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedRevenue && (
          <ScheduleLineTable
            deferredRevenueId={selectedRevenue.id}
            state={selectedRevenue.state}
            onRefresh={fetchData}
          />
        )}
      </Drawer>

      {/* Start Recognition Modal */}
      <Modal
        title="Start Recognition"
        open={startModal}
        onOk={() => form.submit()}
        onCancel={() => {
          setStartModal(false);
          form.resetFields();
        }}
      >
        <Form form={form} layout="vertical" onFinish={handleStartRecognition}>
          <Form.Item
            name="journalId"
            label="Journal"
            rules={[{ required: true, message: 'Please select a journal' }]}
          >
            <Select placeholder="Select journal">
              {journals.map((j) => (
                <Option key={j.id} value={j.id}>
                  {j.code} - {j.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        title="Cancel Deferred Revenue"
        open={cancelModal}
        onOk={() => form.submit()}
        onCancel={() => {
          setCancelModal(false);
          form.resetFields();
        }}
      >
        <Form form={form} layout="vertical" onFinish={handleCancel}>
          <Form.Item
            name="journalId"
            label="Journal"
            rules={[{ required: true, message: 'Please select a journal' }]}
          >
            <Select placeholder="Select journal">
              {journals.map((j) => (
                <Option key={j.id} value={j.id}>
                  {j.code} - {j.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Please enter a reason' }]}
          >
            <Input.TextArea rows={4} placeholder="Enter cancellation reason" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeferredRevenueList;
